import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FaBed, FaBath, FaCar, FaRulerCombined, FaEnvelope, FaSms, FaUser, FaHome, FaChartLine, FaSearch, FaChevronRight, FaClipboardCheck, FaHistory, FaInfoCircle, FaCopy, FaCheck, FaRegCircle, FaCheckCircle, FaStickyNote, FaRobot, FaPaperclip } from "react-icons/fa";
import { handleTokenAndPostRequest } from "../utils/utils";
import { hashEmail } from "./PropertySearchDetail";
import AgentBadge from "../components/AgentBadge";
import { useViewScope } from "../contexts/ViewScopeContext";
import DEMO_PROPERTY_SEARCHES from "../demo/demoPropertySearches";
import DemoBanner from "../demo/DemoBanner";
import useDemoNetworkGuard from "../demo/useDemoNetworkGuard";
import UploadContactsModal from "../components/UploadContactsModal";

function formatCurrency(val) {
  if (val == null || isNaN(val)) return "—";
  return "$" + Number(val).toLocaleString("en-AU", { maximumFractionDigits: 0 });
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function CopyButton({ text }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      title={copied ? "Copied!" : "Copy"}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 2,
        display: "inline-flex",
        alignItems: "center",
        color: copied ? "#22c55e" : "#9ca3af",
        fontSize: 12,
        flexShrink: 0,
        transition: "color 0.2s",
      }}
    >
      {copied ? <FaCheck /> : <FaCopy />}
    </button>
  );
}

export default function PropertySearches({
  embedded = false,
  embeddedSearch = null,
  onCardOpen = null,
} = {}) {
  const [searchParams] = useSearchParams();
  // When rendered as an embedded modal (e.g. from /contacts), force the
  // search to the provided value and ignore URL params.
  const initialSearch = embedded
    ? (embeddedSearch || "")
    : (searchParams.get("search") || "");
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialSearch);
  // When a search query is provided via URL (e.g. coming from the Contacts
  // page "Searches" button), default to showing all statuses so the agent
  // can see every past estimate for that contact, not just to-review ones.
  const [statusFilter, setStatusFilter] = useState(initialSearch ? "all" : "to-review");
  const routerNavigate = useNavigate();
  // When embedded in a modal, route clicks via onCardOpen (which opens a
  // new tab) instead of in-app navigation that would unmount the modal.
  const navigate = onCardOpen || routerNavigate;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [completeMap, setCompleteMap] = useState({});  // key -> [noteId, ...]
  const [markingComplete, setMarkingComplete] = useState(new Set());
  const [cmaMap, setCmaMap] = useState({}); // key (property_id__email) -> [cma, ...]
  const [showUploadModal, setShowUploadModal] = useState(false);
  const { viewScope, agentFilter } = useViewScope();

  async function handleToggleComplete(e, propertyId, email) {
    e.stopPropagation();
    const key = `${propertyId}__${email}`;
    if (markingComplete.has(key)) return;
    const isComplete = (completeMap[key] || []).length > 0;
    setMarkingComplete(prev => new Set(prev).add(key));
    try {
      if (isComplete) {
        // Delete all "Marked as complete" notes for this key
        for (const noteId of completeMap[key]) {
          await handleTokenAndPostRequest(`${window.api_host}/delete-note`, { note_id: noteId });
        }
        setCompleteMap(prev => { const next = { ...prev }; delete next[key]; return next; });
      } else {
        const res = await handleTokenAndPostRequest(`${window.api_host}/save-property-note`, {
          property_id: propertyId,
          user_email: email,
          note: "Marked as complete",
          notify_email: false,
          notify_sms: false,
          visible_to_user: false,
          visible_to_ai: false,
        });
        const newId = res.data?.id || res.data?.note_id;
        setCompleteMap(prev => ({ ...prev, [key]: [...(prev[key] || []), ...(newId ? [newId] : [0])] }));
      }
    } catch (err) {
      console.error("Failed to toggle complete:", err);
    }
    setMarkingComplete(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const agent_id = user.agent_details?.agent_id;
        const agency_id = user.agent_details?.agency_id;
        const res = await handleTokenAndPostRequest(
          `${window.api_host}/property-report-requests`,
          { agent_id, agency_id, view_scope: viewScope, agent_filter: agentFilter }
        );
        const rows = Array.isArray(res.data) ? res.data : [];
        setRawData(rows);
        // Fetch all notes to determine review status
        try {
          const notesRes = await handleTokenAndPostRequest(
            `${window.api_host}/get-notes`, { view_scope: viewScope, agent_filter: agentFilter }
          );
          const notesList = Array.isArray(notesRes.data) ? notesRes.data : [];
          const cMap = {};
          for (const n of notesList) {
            if (n.note === "Marked as complete") {
              const k = `${n.property_id}__${n.user_email}`;
              if (!cMap[k]) cMap[k] = [];
              cMap[k].push(n.id);
            }
          }
          setCompleteMap(cMap);
        } catch {}
        // Fetch all CMAs for the agency so we can render filenames per card
        try {
          const cmaRes = await handleTokenAndPostRequest(
            `${window.api_host}/list-property-cmas`, {}
          );
          const cmaList = Array.isArray(cmaRes.data) ? cmaRes.data : [];
          const m = {};
          for (const c of cmaList) {
            const k = `${c.property_id}__${c.user_email || ''}`;
            if (!m[k]) m[k] = [];
            m[k].push(c);
          }
          setCmaMap(m);
        } catch {}
      } catch (e) {
        setRawData([]);
      }
      setLoading(false);
    }
    fetchData();
  }, [viewScope, agentFilter]);

  // When the agent has no property searches at all (full page, not the
  // embedded contact modal, and not narrowing by a search term), show a
  // populated sample view instead of a bare empty state. Demo interactions are
  // sandboxed by useDemoNetworkGuard and detail navigation is disabled below.
  const showDemo = !embedded && !loading && rawData.length === 0 && !search;
  // When embedded from the /contacts sample view, the contacts page already
  // has window.__demoMode on (so this page's fetch returned nothing). Surface
  // the sample searches filtered to the opened contact instead of an empty
  // modal. Detail navigation is disabled below for both demo paths.
  const embeddedDemo = embedded && typeof window !== "undefined" && window.__demoMode;
  const demoActive = showDemo || embeddedDemo;
  // Relax the guard while the upload modal is open so a real upload can reach
  // the backend.
  useDemoNetworkGuard(showDemo && !showUploadModal);
  const sourceData = demoActive ? DEMO_PROPERTY_SEARCHES : rawData;

  // Group by unique (property_id, email), keep latest entry per combo
  const grouped = useMemo(() => {
    const map = {};
    for (const item of sourceData) {
      const key = `${item.property_id || item.metadata?.property_id || ""}__${item.email || ""}`;
      if (!map[key]) {
        map[key] = { latest: item, count: 1 };
      } else {
        map[key].count += 1;
        const existingDate = new Date(map[key].latest.created_at);
        const newDate = new Date(item.created_at);
        if (newDate > existingDate) {
          map[key].latest = item;
        }
      }
    }
    return Object.values(map)
      .sort((a, b) => new Date(b.latest.created_at) - new Date(a.latest.created_at));
  }, [sourceData]);

  // Filter by search and completion status
  const filtered = useMemo(() => {
    let result = grouped;
    if (statusFilter === "to-review") {
      result = result.filter(({ latest }) => {
        const pid = latest.property_id || latest.metadata?.property_id || "";
        const key = `${pid}__${latest.email}`;
        return !(completeMap[key]?.length > 0);
      });
    } else if (statusFilter === "completed") {
      result = result.filter(({ latest }) => {
        const pid = latest.property_id || latest.metadata?.property_id || "";
        const key = `${pid}__${latest.email}`;
        return (completeMap[key]?.length > 0);
      });
    }
    if (!search) return result;
    const lower = search.toLowerCase();
    return result.filter(({ latest }) => {
      const addr = latest.metadata?.request?.address_text || "";
      const email = latest.email || "";
      const suburb = latest.metadata?.request?.suburb_id || "";
      return (
        addr.toLowerCase().includes(lower) ||
        email.toLowerCase().includes(lower) ||
        suburb.toLowerCase().includes(lower)
      );
    });
  }, [search, grouped, statusFilter, completeMap]);

  return (
    <div style={{
      padding: embedded ? 0 : (isMobile ? "0 16px" : "0 32px"),
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      background: embedded ? "transparent" : "#f6f8fa",
      boxSizing: "border-box",
      width: "100%",
      position: "relative",
      overflow: embedded ? "visible" : "auto",
    }}>
      <div style={{ maxWidth: 900, marginLeft: "auto", marginRight: "auto", width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{
          position: embedded ? "static" : "sticky",
          top: 0,
          zIndex: 10,
          background: embedded ? "transparent" : "#f6f8fa",
          paddingTop: embedded ? 0 : (isMobile ? 60 : 32),
          paddingBottom: 8,
          marginLeft: embedded ? 0 : (isMobile ? -16 : -32),
          marginRight: embedded ? 0 : (isMobile ? -16 : -32),
          paddingLeft: embedded ? 0 : (isMobile ? 16 : 32),
          paddingRight: embedded ? 0 : (isMobile ? 16 : 32),
        }}>
          {!embedded && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <h1 style={{ fontSize: isMobile ? 26 : 32, fontWeight: 700, color: "#1f2937", margin: "0 0 4px 0" }}>Property Searches</h1>
              </div>
              {!isMobile && (
                <p style={{ color: "#6b7280", fontSize: 15, marginBottom: 20, lineHeight: 1.6 }}>
                  Property report downloads and property profile views from your Vendorleads Insights website. Grouped by unique property and visitor email.
                </p>
              )}
            </>
          )}

          {/* Search bar + filter */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: isMobile ? 12 : 24, flexWrap: "wrap" }}>
            {!embedded && (
              <div style={{ position: "relative", maxWidth: 400, flex: 1, minWidth: 200 }}>
                <FaSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 14 }} />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by address, email, or suburb…"
                  style={{
                    width: "100%",
                    padding: "10px 12px 10px 36px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    fontSize: 14,
                    boxSizing: "border-box",
                    background: "#fff",
                    outline: "none",
                  }}
                />
              </div>
            )}
            <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: "1px solid #e5e7eb" }}>
              {["all", "to-review", "completed"].map(val => (
                <button
                  key={val}
                  onClick={() => setStatusFilter(val)}
                  style={{
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    border: "none",
                    borderRight: val !== "completed" ? "1px solid #e5e7eb" : "none",
                    background: statusFilter === val ? "#1f2937" : "#fff",
                    color: statusFilter === val ? "#fff" : "#6b7280",
                    whiteSpace: "nowrap",
                  }}
                >
                  {val === "all" ? "All" : val === "to-review" ? "To Review" : "Completed"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {[...Array(4)].map((_, i) => (
              <div key={i} style={{
                background: "#fff", borderRadius: 16, overflow: "hidden",
                boxShadow: "0 1px 4px #0001", border: "1px solid #e5e7eb",
                display: "flex", flexDirection: isMobile ? "column" : "row",
              }}>
                <div style={{ width: isMobile ? "100%" : 200, minHeight: isMobile ? 180 : 160, borderRight: isMobile ? "none" : "1px solid #e5e7eb", borderBottom: isMobile ? "1px solid #e5e7eb" : "none" }}>
                  <Skeleton height="100%" width="100%" borderRadius={0} style={{ display: "block", minHeight: isMobile ? 180 : 160 }} />
                </div>
                <div style={{ flex: 1, padding: isMobile ? "16px 16px 14px" : "18px 22px", display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Skeleton width={60} height={20} borderRadius={10} />
                    <Skeleton width={80} height={20} borderRadius={10} />
                    <Skeleton width={50} height={20} borderRadius={10} />
                  </div>
                  <Skeleton width="70%" height={16} />
                  <Skeleton width="50%" height={13} />
                  <Skeleton width="40%" height={13} />
                  <Skeleton width="30%" height={12} />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{
            background: "#f8fafc",
            borderRadius: 12,
            padding: 24,
            boxShadow: "0 2px 8px #0001",
            marginBottom: 24,
            border: "1px solid #e5e7eb",
            textAlign: "center",
          }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, color: "#1f2937" }}>
              {rawData.length === 0 ? "No Property Searches Yet" : "No Results Found"}
            </h2>
            <p style={{ fontSize: 15, color: "#6b7280", marginBottom: 0 }}>
              {rawData.length === 0
                ? "When visitors view property profiles or download reports on your website, they'll appear here."
                : "Try adjusting your search terms."}
            </p>
          </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 40 }}>
            {showDemo && (
              <DemoBanner
                isMobile={isMobile}
                title="Here's how Property Searches looks with activity"
                subtitle="Sample data — property views and report downloads from your website appear here automatically."
                ctaLabel="Upload"
                onCta={() => setShowUploadModal(true)}
              />
            )}
            <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: -8 }}>
              {filtered.length} unique propert{filtered.length === 1 ? "y" : "ies"} · {sourceData.length} total event{sourceData.length !== 1 ? "s" : ""}
            </div>
            {filtered.map(({ latest, count }, idx) => {
              const req = latest.metadata?.request || {};
              const meta = latest.metadata || {};
              const isLegacy = !!meta.legacy_alert;
              const addr = req.address_text || "Unknown Address";
              const rawImg = req.media_urls?.[0] || null;
              const heroImg = rawImg && typeof rawImg === 'object' ? (rawImg.largeImage || rawImg.smallImage) : rawImg;
              const pid = latest.property_id || meta.property_id || "";
              const detailUrl = `/leads-property-searches/${hashEmail(latest.email || "")}/${pid}`;

              return (
                <div
                  key={`${latest.property_id}-${latest.email}-${idx}`}
                  onClick={() => { if (showDemo || window.__demoMode) return; navigate(detailUrl); }}
                  style={{
                    background: "#fff",
                    borderRadius: 16,
                    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                    border: "1px solid #e5e7eb",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    cursor: "pointer",
                    transition: "box-shadow 0.2s, transform 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.12)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "none"; }}
                >
                  {/* Property image */}
                  <div style={{
                    width: isMobile ? "100%" : 200,
                    height: isMobile ? 180 : "auto",
                    minHeight: isMobile ? undefined : 160,
                    flexShrink: 0,
                    overflow: "hidden",
                    background: "#f3f4f6",
                    borderRight: isMobile ? "none" : "1px solid #e5e7eb",
                    borderBottom: isMobile ? "1px solid #e5e7eb" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}>
                    {heroImg ? (
                      <img
                        src={heroImg}
                        alt="Property"
                        onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : null}
                    <div style={{ display: heroImg ? "none" : "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", gap: 6 }}>
                      <FaHome style={{ fontSize: 36, color: "#d1d5db" }} />
                      <span style={{ fontSize: 11, color: "#c0c4cc", fontWeight: 500 }}>No image</span>
                    </div>
                  </div>

                  {/* Card content */}
                  <div style={{ flex: 1, padding: isMobile ? "16px 16px 14px" : "18px 22px", display: "flex", flexDirection: "column", minWidth: 0, position: "relative" }}>
                    {/* Mark complete checkbox — top right */}
                    <label
                      style={{ position: "absolute", top: isMobile ? 14 : 16, right: isMobile ? 10 : 20, display: "flex", alignItems: "center", gap: 4, cursor: markingComplete.has(`${pid}__${latest.email}`) ? "default" : "pointer", margin: 0, zIndex: 2, fontSize: 11 }}
                      onClick={e => e.stopPropagation()}
                    >
                      {markingComplete.has(`${pid}__${latest.email}`) ? (
                        <Spinner animation="border" size="sm" style={{ width: 14, height: 14, color: "#9ca3af" }} />
                      ) : (completeMap[`${pid}__${latest.email}`] || []).length > 0 ? (
                        <span style={{ color: "#15803d", fontWeight: 500 }}>Completed</span>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>Complete</span>
                      )}
                      <input
                        type="checkbox"
                        checked={(completeMap[`${pid}__${latest.email}`] || []).length > 0}
                        disabled={markingComplete.has(`${pid}__${latest.email}`)}
                        onChange={e => handleToggleComplete(e, pid, latest.email)}
                        style={{ width: 18, height: 18, accentColor: "#22c55e", cursor: markingComplete.has(`${pid}__${latest.email}`) ? "default" : "pointer" }}
                      />
                    </label>
                    {/* Pills row */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap", paddingRight: isMobile ? 80 : 100 }}>
                      <AgentBadge agentName={latest.agent_name} />
                      <span style={{
                        borderRadius: 16,
                        padding: "1px 10px",
                        fontWeight: 600,
                        fontSize: 11,
                        background: req.report_type === "property_report" ? "#eff6ff" : "#f3e8ff",
                        color: req.report_type === "property_report" ? "#2563eb" : "#7c3aed",
                        border: `1px solid ${req.report_type === "property_report" ? "#bfdbfe" : "#c4b5fd"}`,
                      }}>
                        {req.report_type === "property_report" ? "Report Download" : "Property Analysis"}
                      </span>
                      {req.property_type && (
                        <span style={{
                          borderRadius: 16,
                          padding: "1px 10px",
                          fontWeight: 600,
                          fontSize: 11,
                          background: "#e7fbe9",
                          color: "#15803d",
                          border: "1px solid #22c55e",
                        }}>
                          {req.property_type}
                        </span>
                      )}
                      {count > 1 && (
                        <span style={{
                          borderRadius: 16,
                          padding: "1px 10px",
                          fontWeight: 600,
                          fontSize: 11,
                          background: "#fef3c7",
                          color: "#b45309",
                          border: "1px solid #f59e42",
                        }}>
                          {count} view{count !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>

                    {/* Address */}
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", marginBottom: 4, lineHeight: 1.3, display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{addr}</span>
                      <CopyButton text={addr} />
                    </div>

                    {/* Property features row */}
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 6, fontSize: 12, color: "#6b7280" }}>
                      {req.bedrooms && (
                        <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          <FaBed style={{ fontSize: 11 }} /> {req.bedrooms}
                        </span>
                      )}
                      {req.bathrooms && (
                        <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          <FaBath style={{ fontSize: 11 }} /> {req.bathrooms}
                        </span>
                      )}
                      {req.carspaces && (
                        <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          <FaCar style={{ fontSize: 11 }} /> {req.carspaces}
                        </span>
                      )}
                      {req.land_area && (
                        <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          <FaRulerCombined style={{ fontSize: 11 }} /> {req.land_area} m²
                        </span>
                      )}
                    </div>

                    {/* Contact (name + email + mobile from contacts_merged) */}
                    {(() => {
                      const rawId = latest.email || "";
                      const isPhoneShape = rawId && !rawId.includes("@");
                      const displayEmail = latest.contact_email || (rawId.includes("@") ? rawId : "");
                      const displayMobile = latest.contact_mobile || (isPhoneShape ? rawId : "");
                      const fullName = latest.contact_full_name;
                      return (
                        <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 6 }}>
                          {fullName && (
                            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#1f2937", fontWeight: 600 }}>
                              <FaUser style={{ fontSize: 11, color: "#9ca3af" }} />
                              <span>{fullName}</span>
                            </div>
                          )}
                          {displayEmail && (
                            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#4b5563" }}>
                              <FaEnvelope style={{ fontSize: 11, color: "#9ca3af" }} />
                              <span>{displayEmail}</span>
                              <CopyButton text={displayEmail} />
                            </div>
                          )}
                          {displayMobile && (
                            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#4b5563" }}>
                              <FaSms style={{ fontSize: 11, color: "#9ca3af" }} />
                              <span>{displayMobile}</span>
                              <CopyButton text={displayMobile} />
                            </div>
                          )}
                          {!displayEmail && !displayMobile && (
                            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#4b5563" }}>
                              <FaEnvelope style={{ fontSize: 11, color: "#9ca3af" }} />
                              <span>—</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Legacy alert note */}
                    {isLegacy && (
                      <div style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 6,
                        background: "#fff7ed",
                        border: "1px solid #fed7aa",
                        borderRadius: 8,
                        padding: "6px 10px",
                        marginBottom: 6,
                        fontSize: 11,
                        color: "#9a3412",
                        lineHeight: 1.5,
                      }}>
                        <FaInfoCircle style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }} />
                        <span>Analysis details unavailable — this search occurred before full tracking was introduced.</span>
                      </div>
                    )}

                    {/* Comparable analyses indicator */}
                    {(() => {
                      const comps = (req.comparable_analyses && Array.isArray(req.comparable_analyses) && req.comparable_analyses.length > 0)
                        ? req.comparable_analyses
                        : (req.comparables && Array.isArray(req.comparables) && req.comparables.length > 0)
                          ? req.comparables
                          : null;
                      if (!comps) return null;
                      const hasAnalysis = comps.some(c => c.score != null);
                      return (
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          background: "#f0fdf4",
                          border: "1px solid #bbf7d0",
                          borderRadius: 8,
                          padding: "6px 10px",
                          marginBottom: 6,
                          fontSize: 11,
                          color: "#15803d",
                          fontWeight: 500,
                        }}>
                          <span style={{ fontSize: 12, fontWeight: 700 }}>✓</span>
                          <span>{comps.length} comparable propert{comps.length === 1 ? "y" : "ies"}{hasAnalysis ? " analyzed" : ""}</span>
                        </div>
                      );
                    })()}

                    {/* Uploaded CMA documents */}
                    {(() => {
                      const cmaList = cmaMap[`${pid}__${latest.email || ''}`] || [];
                      if (cmaList.length === 0) return null;
                      return (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                          {cmaList.map(c => (
                            <span
                              key={c.id}
                              title={c.original_filename}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                background: "#eff6ff",
                                color: "#1d4ed8",
                                border: "1px solid #bfdbfe",
                                borderRadius: 16,
                                padding: "2px 10px",
                                fontSize: 11,
                                fontWeight: 600,
                                maxWidth: 240,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <FaPaperclip style={{ fontSize: 10 }} />
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {c.original_filename || "CMA.pdf"}
                              </span>
                            </span>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Estimates row */}
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 6 }}>
                      {meta.valuation != null && (
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          <FaHome style={{ fontSize: 10, marginRight: 3, color: "#2563eb" }} />
                          <span style={{ fontWeight: 600, color: "#1f2937" }}>{formatCurrency(meta.valuation)}</span>
                          {meta.valuation_lower != null && meta.valuation_upper != null && (
                            <span style={{ color: "#9ca3af" }}> ({formatCurrency(meta.valuation_lower)} – {formatCurrency(meta.valuation_upper)})</span>
                          )}
                        </div>
                      )}
                      {meta.rental_estimate != null && (
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          <FaChartLine style={{ fontSize: 10, marginRight: 3, color: "#7c3aed" }} />
                          <span style={{ fontWeight: 600, color: "#1f2937" }}>{formatCurrency(meta.rental_estimate)}/wk</span>
                          {meta.rental_estimate_lower != null && meta.rental_estimate_upper != null && (
                            <span style={{ color: "#9ca3af" }}> ({formatCurrency(meta.rental_estimate_lower)} – {formatCurrency(meta.rental_estimate_upper)})</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Date */}
                    <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
                      {formatDate(latest.created_at)}
                      {meta.valuation_percent_change_year_ago != null && (
                        <span style={{
                          marginLeft: 10,
                          color: meta.valuation_percent_change_year_ago >= 0 ? "#15803d" : "#e11d48",
                          fontWeight: 600,
                        }}>
                          {meta.valuation_percent_change_year_ago >= 0 ? "↑" : "↓"} {Math.abs(meta.valuation_percent_change_year_ago)}% YoY
                        </span>
                      )}
                    </div>

                    {/* Actions row */}
                    <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: isMobile ? 8 : 10, justifyContent: isMobile ? "flex-start" : "flex-end", flexWrap: "wrap" }}>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          if (showDemo || window.__demoMode) return;
                          navigate(`${detailUrl}?note=true`);
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#6b7280",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: 0,
                          whiteSpace: "nowrap",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = "#374151"; }}
                        onMouseLeave={e => { e.currentTarget.style.color = "#6b7280"; }}
                      >
                        <FaStickyNote style={{ fontSize: 11 }} />
                        Add Review Note
                      </button>
                      {meta.ai_summary && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            if (showDemo || window.__demoMode) return;
                            navigate(detailUrl);
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#7c3aed",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            whiteSpace: "nowrap",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; }}
                          onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; }}
                        >
                          <FaRobot style={{ fontSize: 11 }} />
                          View AI Analysis
                        </button>
                      )}
                      {latest.email && (
                        <a
                          href={`/user-activity?email=${encodeURIComponent(latest.email)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#7c3aed",
                            textDecoration: "none",
                            whiteSpace: "nowrap",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; }}
                          onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; }}
                        >
                          <FaHistory style={{ fontSize: 11 }} />
                          View Activity
                        </a>
                      )}
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          const params = new URLSearchParams({ create: "true" });
                          if (addr) params.set("address", addr);
                          if (req.bedrooms) params.set("bedrooms", String(req.bedrooms));
                          if (req.bathrooms) params.set("bathrooms", String(req.bathrooms));
                          if (req.carspaces) params.set("parking", String(req.carspaces));
                          if (req.land_area) params.set("land_size", String(req.land_area));
                          if (req.property_type) params.set("property_type", req.property_type);
                          if (latest.email) params.set("contact_email", latest.email);
                          if (pid) params.set("property_id", pid);
                          navigate(`/leads-property-appraisals?${params.toString()}`);
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#2563eb",
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          borderRadius: 6,
                          padding: "4px 12px",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#dbeafe"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "#eff6ff"; }}
                      >
                        <FaClipboardCheck style={{ fontSize: 11 }} />
                        Create Appraisal
                      </button>
                      <FaChevronRight style={{ fontSize: 12, color: "#9ca3af" }} />
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Spacer so the last card clears the fixed demo banner. */}
            {showDemo && <div style={{ height: isMobile ? 180 : 100 }} />}
          </div>
        )}
      </div>
      <UploadContactsModal show={showUploadModal} onClose={() => setShowUploadModal(false)} />
    </div>
  );
}